// Ops Requests widget — Robert's queue at the top of his board (Sep 25, 2026).
//
// TRUE counts in the header (full fetch of what's open and assigned to me,
// display slice below), urgent + overdue first, and a failed read is never
// rendered as "all clear".

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";
import { useRealtimeTable } from "../../../lib/useRealtimeTable";
import {
  CATEGORY_ICON, CATEGORY_LABEL, OPEN_STATUSES, agingLabel, agingTone, sortForQueue,
  type OpsRequest,
} from "../../../lib/opsRequests";

const SHOW = 4;

export default function OpsRequestsWidget({ memberId }: { memberId: string }) {
  const [rows, setRows] = useState<OpsRequest[] | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("ops_requests")
      .select("*")
      .eq("assigned_to", memberId)
      .in("status", OPEN_STATUSES);
    if (error) { setFailed(true); return; }
    const list = (data ?? []) as OpsRequest[];
    setRows(list);
    const ids = [...new Set(list.map((r) => r.requested_by))];
    if (ids.length) {
      const { data: people } = await supabase.from("team_roster").select("id, full_name").in("id", ids);
      setNames(Object.fromEntries(((people ?? []) as Array<{ id: string; full_name: string }>).map((p) => [p.id, p.full_name])));
    }
  }, [memberId]);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("ops_requests", () => { void load(); });

  const sorted = rows ? sortForQueue(rows) : [];
  const urgent = rows ? rows.filter((r) => r.priority === "urgent").length : 0;
  const overdue = rows ? rows.filter((r) => agingTone(r) === "red").length : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span>🛠</span>
        <span className="font-bold text-sm text-bardin-ink">Ops Requests</span>
        <span className="ml-auto text-xs font-bold text-slate-500 tabular-nums">
          {failed ? "couldn't load" : rows == null ? "…" : `${rows.length} open${urgent ? ` · ${urgent} urgent` : ""}${overdue ? ` · ${overdue} overdue` : ""}`}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-rose-700">Couldn't load your requests — open the board to check.</div>
      ) : rows == null ? (
        <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500">Nothing waiting on you. Reps file requests from the job page.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {sorted.slice(0, SHOW).map((r) => {
            const tone = agingTone(r);
            return (
              <Link key={r.id} to={`/requests?focus=${r.id}`} onClick={() => logCardClick("widget_opsreq_row")}
                    className="flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-slate-50 -mx-1 px-1 rounded">
                <div className="min-w-0">
                  <div className="font-semibold text-bardin-ink truncate">
                    {r.homeowner_name || "(no name)"}{r.property_address ? <span className="font-normal text-slate-500"> · {r.property_address}</span> : null}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {CATEGORY_ICON[r.category]} {CATEGORY_LABEL[r.category]} · {names[r.requested_by] ?? "—"}
                    {r.status !== "new" ? ` · ${r.status === "in_progress" ? "in progress" : "waiting"}` : ""}
                  </div>
                </div>
                <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                  r.priority === "urgent" ? "bg-rose-100 text-rose-700"
                  : tone === "red" ? "bg-rose-100 text-rose-700"
                  : tone === "amber" ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-600"}`}>
                  {r.priority === "urgent" ? "URGENT" : agingLabel(r)}
                </span>
              </Link>
            );
          })}
        </div>
      )}

      <Link to="/requests" onClick={() => logCardClick("widget_opsreq_open")}
            className="block text-[11px] font-bold text-bardin-orange mt-2">
        {rows && rows.length > SHOW ? `+${rows.length - SHOW} more · ` : ""}Open the list ›
      </Link>
    </div>
  );
}
